/**
 * ReceptionListPage Component
 * Main page for reception management
 *
 * @version 2025.1.0
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  Autocomplete,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Chip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { format, parseISO } from 'date-fns';
import {
  Refresh as RefreshIcon,
  FileDownload as ExportIcon,
  TableChart as TableIcon,
  CalendarMonth as CalendarIcon,
  Description as CsvIcon,
  Tv as TvIcon,
  Login as CheckInIcon,
  Cancel as NoShowIcon,
  Edit as EditIcon,
  FlashOn as QuickIcon,
  LocalHospital as TriageIcon,
  PictureAsPdf as GuideIcon,
  Share as ShareIcon,
  Science as ScienceIcon,
  Print as PrintIcon,
  Campaign as CallIcon,
} from '@mui/icons-material';

import { useReception, useReceptionExport, ExportFormat } from '../../hooks';
import { useEscolhaUnica } from '../../hooks/useEscolhaUnica';
import {
  ReceptionAppointment,
  AttendanceStatus,
  AttendanceType,
  formatQueueNumber,
  formatTime,
} from '../../types/reception';
import { APPOINTMENT_STATUS_COLORS } from '../../types/appointment';
import { TriageData } from '../../types/triage';
import { Company } from '../../types/company';
import {
  AttendanceStatusBadge,
  ReceptionFilters,
  ReceptionSummaryCards,
  ReceptionCalendarView,
  CheckInDialog,
  QuickAttendanceWizard,
  PatientDetailDialog,
  TriageOnlyDialog,
} from './components';
import { QueueEditDialog } from './components/QueueEditDialog';
import { useSnackbar } from 'notistack';
import companyService from '../../services/companyService';
import appointmentService from '../../services/appointmentService';
import { receptionService } from '../../services/receptionService';
import {
  ActionButton,
  PageShell,
  DataTable,
  HelpButton,
  type DataTableColumn,
  type PrerequisiteBlockProps,
} from '../../components/common';
import { ShareLinkDialog } from '../../components/dialogs/ShareLinkDialog';
import { openSignedFile } from '../../services/fileAccess';

// View mode type
type ViewMode = 'table' | 'calendar';

// Clinic interface for autocomplete
interface Clinic {
  id: number;
  tradeName: string;
}

/** Senha impressa/chamável: já fez check-in e tem número de senha. */
const hasQueueTicket = (appointment: ReceptionAppointment): boolean =>
  appointment.queueNumber !== null &&
  (appointment.attendanceStatus === AttendanceStatus.CHECKED_IN ||
    appointment.attendanceStatus === AttendanceStatus.TRIAGED ||
    appointment.attendanceStatus === AttendanceStatus.IN_PROGRESS);

const canResetQueue = (appointment: ReceptionAppointment): boolean =>
  (appointment.attendanceStatus === AttendanceStatus.CHECKED_IN ||
    appointment.attendanceStatus === AttendanceStatus.TRIAGED) &&
  appointment.queueNumber !== null;

const canGenerateGuide = (appointment: ReceptionAppointment): boolean =>
  appointment.attendanceStatus === AttendanceStatus.CHECKED_IN ||
  appointment.attendanceStatus === AttendanceStatus.TRIAGED ||
  appointment.attendanceStatus === AttendanceStatus.IN_PROGRESS ||
  appointment.attendanceStatus === AttendanceStatus.COMPLETED;

const hasServiceAction = (
  appointment: ReceptionAppointment,
  action: 'check-in' | 'no-show',
): boolean => receptionService.getAvailableActions(appointment.attendanceStatus).includes(action);

export const ReceptionListPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [clinics, setClinics] = useState<Clinic[]>([]);
  // Lista vazia por ainda não ter chegado é indistinguível de lista vazia de verdade, e os
  // filtros se desenhavam duas vezes ao abrir a tela.
  const [empresasResolvidas, setEmpresasResolvidas] = useState(false);
  const [clinicasResolvidas, setClinicasResolvidas] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState<Clinic | null>(null);
  const [checkInDialogOpen, setCheckInDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<ReceptionAppointment | null>(null);
  const [checkInLoading, setCheckInLoading] = useState(false);
  const [queueEditDialogOpen, setQueueEditDialogOpen] = useState(false);
  const [queueEditAppointment, setQueueEditAppointment] = useState<ReceptionAppointment | null>(null);
  const [queueEditLoading, setQueueEditLoading] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardAppointment, setWizardAppointment] = useState<ReceptionAppointment | null>(null);
  const [patientDetailOpen, setPatientDetailOpen] = useState(false);
  const [patientDetailAppointment, setPatientDetailAppointment] = useState<ReceptionAppointment | null>(null);
  const [triageDialogOpen, setTriageDialogOpen] = useState(false);
  const [triageAppointment, setTriageAppointment] = useState<ReceptionAppointment | null>(null);
  const [triageLoading, setTriageLoading] = useState(false);
  const [shareLinkDialogOpen, setShareLinkDialogOpen] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [shareLinkExpiry, setShareLinkExpiry] = useState<Date | undefined>(undefined);
  const [sharePatientName, setSharePatientName] = useState<string | undefined>(undefined);

  // View mode handler
  const handleViewModeChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, newMode: ViewMode | null) => {
      if (newMode) {
        setViewMode(newMode);
      }
    },
    []
  );

  // Hooks
  const {
    appointments,
    summary,
    loading,
    summaryLoading,
    filters,
    pagination,
    socketConnected,
    updateFilter,
    clearFilters,
    setPage,
    setLimit,
    refresh,
    checkIn,
    saveTriageOnly,
    startAttendance,
    completeAttendance,
    markNoShow,
    updateQueueNumber,
    resetQueue,
  } = useReception({
    initialFilters: {
      date: new Date().toISOString().split('T')[0],
      period: 'all',
    },
    autoLoad: true,
  });

  const { exporting, exportReport } = useReceptionExport();

  // Calendar date change handler
  const handleCalendarDateChange = useCallback(
    (date: Date) => {
      updateFilter('date', format(date, 'yyyy-MM-dd'));
    },
    [updateFilter]
  );

  // Local state for instant chip visual feedback
  const [chipSelected, setChipSelected] = useState<AttendanceStatus[]>([]);

  // Sync from external filter changes (e.g., dropdown)
  useEffect(() => {
    const v = filters.attendanceStatus;
    setChipSelected(Array.isArray(v) ? v : v ? [v] : []);
  }, [filters.attendanceStatus]);

  // Status chip click handler (toggle filter)
  const handleStatusChipClick = useCallback(
    (status: AttendanceStatus) => {
      setChipSelected(prev => {
        const next = prev.includes(status)
          ? prev.filter(s => s !== status)
          : [...prev, status];
        updateFilter('attendanceStatus', next.length > 0 ? next : undefined);
        return next;
      });
    },
    [updateFilter]
  );

  // Date change handler
  const handleDateChange = useCallback(
    (date: Date | null) => {
      if (date) {
        updateFilter('date', format(date, 'yyyy-MM-dd'));
      }
    },
    [updateFilter]
  );

  // Load companies on mount
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const response = await companyService.getCompanies({ limit: 100 });
        setCompanies(response.data ?? []);
      } catch (error) {
        console.error('Error loading companies:', error);
      } finally {
        setEmpresasResolvidas(true);
      }
    };
    loadCompanies();
  }, []);

  const selecionarEmpresa = useCallback(
    (company: Company | null) => {
      setSelectedCompany(company);
      updateFilter('companyId', company?.id ? Number(company.id) : undefined);
    },
    [updateFilter]
  );

  const selecionarClinica = useCallback(
    (clinic: Clinic | null) => {
      setSelectedClinic(clinic);
      updateFilter('clinicId', clinic?.id);
    },
    [updateFilter]
  );

  const { mostrarSeletor: mostrarSeletorDeEmpresa } = useEscolhaUnica(
    companies,
    selectedCompany,
    selecionarEmpresa,
    { resolvida: empresasResolvidas }
  );
  const { mostrarSeletor: mostrarSeletorDeClinica } = useEscolhaUnica(
    clinics,
    selectedClinic,
    selecionarClinica,
    { resolvida: clinicasResolvidas }
  );

  // Export menu handlers
  const handleExportClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  }, []);

  const handleExportClose = useCallback(() => {
    setExportMenuAnchor(null);
  }, []);

  const handleExportFormat = useCallback(async (format: ExportFormat) => {
    handleExportClose();
    await exportReport(filters, format);
  }, [exportReport, filters, handleExportClose]);

  // Check-in dialog handlers
  const handleCheckInClick = useCallback((appointmentId: number) => {
    const appointment = appointments.find(a => a.id === appointmentId);
    if (appointment) {
      setSelectedAppointment(appointment);
      setCheckInDialogOpen(true);
    }
  }, [appointments]);

  const handleCheckInConfirm = useCallback(async () => {
    if (!selectedAppointment) return;

    setCheckInLoading(true);
    const success = await checkIn(selectedAppointment.id);
    setCheckInLoading(false);

    if (success) {
      setCheckInDialogOpen(false);
      setSelectedAppointment(null);
    }
  }, [selectedAppointment, checkIn]);

  const handleCheckInClose = useCallback(() => {
    setCheckInDialogOpen(false);
    setSelectedAppointment(null);
  }, []);

  // Queue edit dialog handlers
  const handleEditQueue = useCallback((appointment: ReceptionAppointment) => {
    setQueueEditAppointment(appointment);
    setQueueEditDialogOpen(true);
  }, []);

  const handleQueueEditConfirm = useCallback(async (newQueueNumber: number) => {
    if (!queueEditAppointment) return;

    setQueueEditLoading(true);
    const success = await updateQueueNumber(queueEditAppointment.id, newQueueNumber);
    setQueueEditLoading(false);

    if (success) {
      setQueueEditDialogOpen(false);
      setQueueEditAppointment(null);
    }
  }, [queueEditAppointment, updateQueueNumber]);

  const handleQueueEditClose = useCallback(() => {
    setQueueEditDialogOpen(false);
    setQueueEditAppointment(null);
  }, []);

  const handleResetQueue = useCallback(async (appointmentId: number) => {
    await resetQueue(appointmentId);
  }, [resetQueue]);

  // Triage dialog handlers
  const handleTriageClick = useCallback((appointment: ReceptionAppointment) => {
    setTriageAppointment(appointment);
    setTriageDialogOpen(true);
  }, []);

  const handleTriageConfirm = useCallback(async (triageData: TriageData) => {
    if (!triageAppointment) return;

    setTriageLoading(true);
    const success = await saveTriageOnly(triageAppointment.id, triageData);
    setTriageLoading(false);

    if (success) {
      setTriageDialogOpen(false);
      setTriageAppointment(null);
    }
  }, [triageAppointment, saveTriageOnly]);

  const handleTriageClose = useCallback(() => {
    setTriageDialogOpen(false);
    setTriageAppointment(null);
  }, []);

  // Quick attendance wizard handlers
  const handleWizardClose = useCallback(() => {
    setWizardOpen(false);
    setWizardAppointment(null);
  }, []);

  const handleWizardComplete = useCallback(() => {
    setWizardOpen(false);
    setWizardAppointment(null);
    refresh();
  }, [refresh]);

  // Direct action handlers (without dialog)
  const handleStartAttendance = useCallback(
    async (appointmentId: number) => {
      await startAttendance(appointmentId);
    },
    [startAttendance]
  );

  const handleCompleteAttendance = useCallback(
    async (appointmentId: number): Promise<PrerequisiteBlockProps | null> => {
      let blocked: PrerequisiteBlockProps | null = null;
      await completeAttendance(appointmentId, undefined, (block) => {
        blocked = block;
      });
      return blocked;
    },
    [completeAttendance]
  );

  const handleMarkNoShow = useCallback(
    async (appointmentId: number) => {
      await markNoShow(appointmentId);
    },
    [markNoShow]
  );

  // Row click handler - open patient detail dialog
  const handleRowClick = useCallback(
    (appointmentId: number) => {
      const appointment = appointments.find(a => a.id === appointmentId);
      if (appointment) {
        setPatientDetailAppointment(appointment);
        setPatientDetailOpen(true);
      }
    },
    [appointments]
  );

  // Patient detail dialog handlers
  const handlePatientDetailClose = useCallback(() => {
    setPatientDetailOpen(false);
    setPatientDetailAppointment(null);
  }, []);

  const handleCallPatient = useCallback(async (appointmentId: number) => {
    await receptionService.callPatient(appointmentId);
    refresh();
  }, [refresh]);

  const handlePrintTicket = useCallback(async (appointmentId: number) => {
    const appointment = appointments.find(a => a.id === appointmentId);
    if (!appointment || !appointment.queueNumber) return;

    const time = !appointment.hour && appointment.period === 'morning'
      ? 'Manhã'
      : !appointment.hour && appointment.period === 'afternoon'
        ? 'Tarde'
        : `${appointment.hour?.padStart(2, '0') || '00'}:${(appointment.minute ?? '0').padStart(2, '0')}`;

    const ticketData: import('../../types/printer').TicketData = {
      queueNumber: appointment.queueNumber,
      patientName: appointment.employeeName,
      companyName: appointment.companyName ?? null,
      examType: appointment.examType ?? null,
      time,
      date: new Date().toLocaleDateString('pt-BR'),
      providerName: appointment.clinicName ?? 'Clínica',
    };

    // Try network printing first
    try {
      const { printerService } = await import('../../services/printerService');
      const result = await printerService.printQueueTicket(appointmentId);
      if (result.success && result.method === 'network') {
        enqueueSnackbar('Senha impressa com sucesso', { variant: 'success' });
        return;
      }
    } catch {
      // Network print failed or unavailable — fall through to browser print
    }

    // Fallback: browser print
    const { printTicketViaBrowser } = await import('../../utils/printTicket');
    printTicketViaBrowser(ticketData);
  }, [appointments, enqueueSnackbar]);

  // Generate Guide PDF handler
  const handleGenerateGuide = useCallback(async (appointmentId: number) => {
    const target = window.open('about:blank', '_blank');
    try {
      const result = await receptionService.generateGuide(appointmentId);
      if (result.pdfPath) {
        if (target) {
          try {
            await openSignedFile(result.pdfPath, target);
          } catch (err) {
            target.close();
            enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao abrir Guia', { variant: 'error' });
          }
        }
        enqueueSnackbar('Guia gerada com sucesso', { variant: 'success' });
      } else if (target) {
        target.close();
      }
    } catch (error) {
      if (target) target.close();
      console.error('Error generating guide:', error);
      enqueueSnackbar('Erro ao gerar guia', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // Share Kit link handler
  const handleShareKit = useCallback(async (appointment: ReceptionAppointment) => {
    try {
      const result = await receptionService.generateKitLink(appointment.id);
      setShareLink(result.link);
      setShareLinkExpiry(new Date(result.expiresAt));
      setSharePatientName(appointment.employeeName);
      setShareLinkDialogOpen(true);
    } catch (error) {
      console.error('Error generating kit link:', error);
      enqueueSnackbar('Erro ao gerar link do kit', { variant: 'error' });
    }
  }, [enqueueSnackbar]);

  // Load clinics when selected company changes
  useEffect(() => {
    const loadClinics = async () => {
      if (!selectedCompany) {
        setClinics([]);
        setClinicasResolvidas(true);
        return;
      }
      setClinicasResolvidas(false);
      try {
        const data = await appointmentService.getClinicsForCompany(Number(selectedCompany.id));
        setClinics(data.map(c => ({ id: c.id, tradeName: c.displayName })));
      } catch (error) {
        console.error('Error loading clinics:', error);
        setClinics([]);
      } finally {
        setClinicasResolvidas(true);
      }
    };
    loadClinics();
  }, [selectedCompany]);

  const selectedDate = filters.date ? parseISO(filters.date) : new Date();

  const columns: DataTableColumn<ReceptionAppointment>[] = [
    {
      id: 'queueNumber',
      label: 'Senha',
      width: 80,
      render: (appointment) =>
        appointment.attendanceStatus === AttendanceStatus.CHECKED_IN &&
        appointment.queueNumber !== null ? (
          <Tooltip title="Clique para editar senha">
            <Box
              onClick={(event) => {
                event.stopPropagation();
                handleEditQueue(appointment);
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                  borderRadius: 1,
                },
                px: 0.5,
                py: 0.25,
              }}
            >
              <Typography variant="body2" fontWeight="bold" color="primary">
                {formatQueueNumber(appointment.queueNumber)}
              </Typography>
              <EditIcon sx={{ fontSize: 14, color: 'action.active' }} />
            </Box>
          </Tooltip>
        ) : (
          <Typography
            variant="body2"
            fontWeight="bold"
            color={appointment.queueNumber ? 'primary' : 'text.secondary'}
          >
            {formatQueueNumber(appointment.queueNumber)}
          </Typography>
        ),
    },
    {
      id: 'time',
      label: 'Horário',
      width: 80,
      render: (appointment) => (
        <Typography variant="body2">
          {formatTime(appointment.hour, appointment.minute, appointment.period)}
        </Typography>
      ),
    },
    {
      id: 'employee',
      label: 'Funcionário',
      primary: true,
      render: (appointment) => (
        <>
          <Typography variant="body2" fontWeight="medium">
            {appointment.employeeName}
          </Typography>
          {appointment.employeeCpf && (
            <Typography variant="caption" color="text.secondary">
              {appointment.employeeCpf}
            </Typography>
          )}
        </>
      ),
    },
    {
      id: 'company',
      label: 'Empresa',
      render: (appointment) => (
        <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
          {appointment.companyName}
        </Typography>
      ),
    },
    {
      id: 'examType',
      label: 'Tipo Exame',
      width: 140,
      render: (appointment) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="body2">{appointment.examType}</Typography>
          {appointment.attendanceType === AttendanceType.QUICK && (
            <Chip
              icon={<QuickIcon sx={{ fontSize: '14px !important' }} />}
              label="Rapido"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
            />
          )}
          {(appointment.appointmentExams?.length ?? 0) > 0 && (
            <Chip
              icon={<ScienceIcon sx={{ fontSize: '14px !important' }} />}
              label={appointment.appointmentExams!.length}
              size="small"
              color="secondary"
              variant="outlined"
              sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem' } }}
            />
          )}
        </Box>
      ),
    },
    {
      id: 'appointmentStatus',
      label: 'Status Agend.',
      width: 120,
      render: (appointment) => (
        <Chip
          label={appointment.status}
          size="small"
          color={
            APPOINTMENT_STATUS_COLORS[
              appointment.status as keyof typeof APPOINTMENT_STATUS_COLORS
            ] || 'default'
          }
        />
      ),
    },
    {
      id: 'attendanceStatus',
      label: 'Status Atend.',
      width: 130,
      render: (appointment) => (
        <AttendanceStatusBadge
          status={appointment.attendanceStatus || AttendanceStatus.NOT_ATTENDED}
        />
      ),
    },
  ];

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Medicina' },
        { label: 'Recepção' },
      ]}
      title="Recepção"
      actions={
        <>
          <HelpButton entity="reception" screen="lista" size="small" />
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="table" aria-label="visualizacao tabela">
              <Tooltip title="Tabela">
                <TableIcon />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="calendar" aria-label="visualização calendário">
              <Tooltip title="Calendário">
                <CalendarIcon />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Painel de Senhas">
            <IconButton onClick={() => {
              const clinicId = selectedClinic?.id ?? 1;
              window.open(`/painel?clinicId=${clinicId}`, 'painel-senhas', 'width=1024,height=768');
            }}>
              <TvIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={socketConnected ? 'Tempo real ativo' : 'Tempo real desconectado'}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: socketConnected ? 'success.main' : 'warning.main',
                alignSelf: 'center',
              }}
            />
          </Tooltip>
          <Tooltip title="Atualizar">
            <IconButton onClick={refresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <ActionButton
            acao="reception.export"
            rotulo="Exportar"
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExportClick}
            disabled={exporting || loading}
          >
            {exporting ? 'Exportando...' : 'Exportar'}
          </ActionButton>
          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={handleExportClose}
          >
            <MenuItem onClick={() => handleExportFormat('xlsx')}>
              <ListItemIcon>
                <TableIcon fontSize="small" />
              </ListItemIcon>
              Excel (.xlsx)
            </MenuItem>
            <MenuItem onClick={() => handleExportFormat('csv')}>
              <ListItemIcon>
                <CsvIcon fontSize="small" />
              </ListItemIcon>
              CSV (.csv)
            </MenuItem>
          </Menu>
        </>
      }
    >
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
        {/* Date, Company and Clinic Selection */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
            <DatePicker
              label="Data"
              value={selectedDate}
              onChange={handleDateChange}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { width: 180 },
                },
              }}
            />
            {mostrarSeletorDeEmpresa && (
              <Autocomplete
                options={companies}
                getOptionLabel={(option) => (option.tradeName || option.legalName) ?? ''}
                value={selectedCompany}
                onChange={(_, empresa) => selecionarEmpresa(empresa)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Empresa"
                    size="small"
                    sx={{ width: 300 }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
              />
            )}
            {mostrarSeletorDeClinica && (
              <Autocomplete
                options={clinics}
                getOptionLabel={(option) => option.tradeName}
                value={selectedClinic}
                onChange={(_, clinica) => selecionarClinica(clinica)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Clínica"
                    size="small"
                    sx={{ width: 250 }}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            )}
          </Box>
        </Paper>

        {/* Summary Cards */}
        <ReceptionSummaryCards
          summary={summary}
          loading={summaryLoading}
          onStatusClick={handleStatusChipClick}
          selectedStatuses={chipSelected}
        />

        {/* Filters - only show in table mode */}
        {viewMode === 'table' && (
          <ReceptionFilters
            filters={filters}
            onFilterChange={updateFilter}
            onClear={clearFilters}
            loading={loading}
            total={pagination.total}
          />
        )}

        {/* Table View */}
        {viewMode === 'table' && (
          <DataTable
            columns={columns}
            rows={appointments}
            getRowId={(appointment) => appointment.id}
            loading={loading}
            emptyState={{
              title: 'Nenhum agendamento encontrado',
              description:
                'A recepção mostra os agendamentos do período filtrado, para o check-in dos pacientes na chegada.',
            }}
            onRowClick={(appointment) => handleRowClick(appointment.id)}
            actions={[
              {
                icon: <CheckInIcon fontSize="small" />,
                tooltip: 'Check-in',
                color: 'info',
                onClick: (appointment) => handleCheckInClick(appointment.id),
                hidden: (appointment) => !hasServiceAction(appointment, 'check-in'),
              },
              {
                icon: <TriageIcon fontSize="small" />,
                tooltip: 'Registrar Triagem',
                color: 'secondary',
                onClick: handleTriageClick,
                hidden: (appointment) =>
                  appointment.attendanceStatus !== AttendanceStatus.CHECKED_IN,
              },
              {
                icon: <PrintIcon fontSize="small" />,
                tooltip: 'Imprimir Senha',
                color: 'default',
                onClick: (appointment) => handlePrintTicket(appointment.id),
                hidden: (appointment) => !hasQueueTicket(appointment),
              },
              {
                icon: <CallIcon fontSize="small" />,
                tooltip: 'Chamar Paciente',
                color: 'warning',
                onClick: (appointment) => handleCallPatient(appointment.id),
                hidden: (appointment) => !hasQueueTicket(appointment),
              },
              {
                icon: <NoShowIcon fontSize="small" />,
                tooltip: 'Marcar Falta',
                color: 'error',
                onClick: (appointment) => handleMarkNoShow(appointment.id),
                hidden: (appointment) => !hasServiceAction(appointment, 'no-show'),
              },
              {
                icon: <RefreshIcon fontSize="small" />,
                tooltip: 'Resetar Senha',
                color: 'default',
                onClick: (appointment) => handleResetQueue(appointment.id),
                hidden: (appointment) => !canResetQueue(appointment),
              },
              {
                icon: <GuideIcon fontSize="small" />,
                tooltip: 'Gerar Guia PDF',
                color: 'info',
                onClick: (appointment) => handleGenerateGuide(appointment.id),
                hidden: (appointment) => !canGenerateGuide(appointment),
              },
              {
                icon: <ShareIcon fontSize="small" />,
                tooltip: 'Compartilhar Kit',
                color: 'primary',
                onClick: handleShareKit,
                hidden: (appointment) =>
                  appointment.attendanceStatus !== AttendanceStatus.COMPLETED,
              },
            ]}
            pagination={{
              page: pagination.page - 1, // o hook usa 1-based, o DataTable 0-based
              rowsPerPage: pagination.limit,
              totalCount: pagination.total,
              onPageChange: (newPage) => setPage(newPage + 1),
              onRowsPerPageChange: setLimit,
              rowsPerPageOptions: [25, 50, 100],
            }}
          />
        )}

        {/* Calendar View */}
        {viewMode === 'calendar' && (
          <ReceptionCalendarView
            appointments={appointments}
            loading={loading}
            selectedDate={selectedDate}
            onDateChange={handleCalendarDateChange}
            onCheckIn={handleCheckInClick}
            onStartAttendance={handleStartAttendance}
            onCompleteAttendance={handleCompleteAttendance}
            onMarkNoShow={handleMarkNoShow}
          />
        )}

        {/* Check-in Dialog */}
        <CheckInDialog
          open={checkInDialogOpen}
          appointment={selectedAppointment}
          loading={checkInLoading}
          onConfirm={handleCheckInConfirm}
          onClose={handleCheckInClose}
        />

        {/* Queue Edit Dialog */}
        <QueueEditDialog
          open={queueEditDialogOpen}
          appointment={queueEditAppointment}
          loading={queueEditLoading}
          onConfirm={handleQueueEditConfirm}
          onClose={handleQueueEditClose}
        />

        {/* Quick Attendance Wizard */}
        <QuickAttendanceWizard
          open={wizardOpen}
          appointment={wizardAppointment}
          onClose={handleWizardClose}
          onComplete={handleWizardComplete}
          checkIn={checkIn}
          startAttendance={startAttendance}
          completeAttendance={completeAttendance}
        />

        {/* Patient Detail Dialog */}
        <PatientDetailDialog
          open={patientDetailOpen}
          appointment={patientDetailAppointment}
          onClose={handlePatientDetailClose}
          onCallPatient={handleCallPatient}
          onPrintTicket={handlePrintTicket}
        />

        {/* Triage Only Dialog */}
        <TriageOnlyDialog
          open={triageDialogOpen}
          appointment={triageAppointment}
          loading={triageLoading}
          onConfirm={handleTriageConfirm}
          onClose={handleTriageClose}
        />

        {/* Share Kit Link Dialog */}
        <ShareLinkDialog
          open={shareLinkDialogOpen}
          onClose={() => setShareLinkDialogOpen(false)}
          link={shareLink}
          expiresAt={shareLinkExpiry}
          patientName={sharePatientName}
        />
      </LocalizationProvider>
    </PageShell>
  );
};

export default ReceptionListPage;
